import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { saveCognitiveRecord } from '@services/airtable';
import connectCognitiveAugment from '@assets/logos/connect_cognitive_augment.png';

// ─── Types ────────────────────────────────────────────────────────────────────
type NotificationType = 'success' | 'error';

interface FormData {
  account: string;
  name: string;
  role: string;
  answers: Record<string, number>;
}

// ─── Notification Component ───────────────────────────────────────────────────
const Notification = ({
  show,
  type,
  message,
  onClose
}: {
  show: boolean;
  type: NotificationType;
  message: string;
  onClose: () => void;
}) => (
  <AnimatePresence>
    {show && (
      <motion.div
        initial={{ opacity: 0, y: -40, x: '-50%' }}
        animate={{ opacity: 1, y: 0, x: '-50%' }}
        exit={{ opacity: 0, y: -40, x: '-50%' }}
        className="fixed top-4 sm:top-6 left-1/2 z-50 w-[calc(100vw-1.5rem)] max-w-md px-4 sm:px-6 py-3 sm:py-4 rounded-xl border shadow-2xl backdrop-blur-md"
      >
        <div className="flex items-center gap-3">
          <span className="text-xl shrink-0">{type === 'success' ? '✅' : '❌'}</span>
          <p className={`text-sm font-medium ${type === 'success' ? 'text-green-300' : 'text-red-300'}`}>{message}</p>
          <button onClick={onClose} className="ml-auto shrink-0 text-gray-400 hover:text-white transition-colors">
            ✕
          </button>
        </div>
      </motion.div>
    )}
  </AnimatePresence>
);

// ─── Questions Data ───────────────────────────────────────────────────────────
const BLOCKS = [
  {
    id: 'memoria',
    title: 'Pilar 1: Búfer de Memoria',
    color: '#ea00d9',
    description: 'Evalúa tu capacidad para retener datos a corto plazo y recordar detalles sin confundirlos.',
    questions: [
      'Al revisar un documento largo, ¿te encuentras retrocediendo repetidamente para volver a comprobar definiciones o datos?',
      '¿Frecuentemente sabes que has leído algo importante pero eres incapaz de recordar dónde está o el dato exacto?',
      'Cuando aprendes algo nuevo o cambias de proyecto, ¿sientes que se te borran o confunden los detalles del proyecto anterior?'
    ]
  },
  {
    id: 'atencion',
    title: 'Pilar 2: Filtro de Atención',
    color: '#d4b500',
    description: 'Evalúa tu resistencia a las distracciones, el costo de interrumpirte y tu desgaste de energía.',
    questions: [
      'Si intentas leer o trabajar enfocado por media hora, ¿tu mente divaga hacia otros temas más de un par de veces?',
      'Si te interrumpen por cinco minutos, ¿sientes que te toma muchísimo esfuerzo volver a concentrarte en lo que estabas haciendo?',
      '¿Sientes que tomas peores decisiones, evitas pensar o estás desproporcionadamente agotado mentalmente al final de la tarde?'
    ]
  },
  {
    id: 'razonamiento',
    title: 'Pilar 3: Procesador Lógico (Sistema 2)',
    color: '#00b8d4',
    description: 'Evalúa tu capacidad para estructurar planes, conectar ideas y resolver problemas complejos.',
    questions: [
      '¿Tus planes o proyectos suelen fallar a la mitad porque aparecieron tareas sorpresa o dependencias que no calculaste al inicio?',
      'Al enfrentar un problema nuevo, ¿te sientes en blanco en lugar de conectarlo rápidamente con otros problemas similares que ya resolviste?',
      'Si tienes que resumir información de varios lugares, ¿el resultado termina siendo una lista suelta de datos en vez de una idea conectada y clara?'
    ]
  },
  {
    id: 'base',
    title: 'Pilar 4: Base Cognitiva (Inteligencias)',
    color: '#ff6b35',
    description:
      'Evalúa tus inteligencias base según el perfil: kinestésica-manual, espacial-visual, corporal y otras formas de procesar el mundo.',
    questions: [
      'Cuando aprendes una habilidad manual o física (ensamblar, dibujar, manipular herramientas), ¿te cuesta más de lo normal coordinar tus movimientos con lo que quieres lograr?',
      'Al imaginar objetos, mapas o espacios en tu mente (inteligencia espacial-visual), ¿te resulta difícil rotarlos o visualizarlos con claridad?',
      '¿Sientes que aprendes o retienes mejor haciendo y tocando las cosas que solo leyéndolas o escuchándolas, pero pocas veces tienes esa oportunidad?'
    ]
  }
];

const SCALE_OPTIONS = [
  { value: 0, label: 'No es un problema', short: '0' },
  { value: 1, label: 'Molestia leve', short: '1' },
  { value: 2, label: 'Freno constante', short: '2' },
  { value: 3, label: 'Bloqueo crítico', short: '3' }
];

const ACCOUNTS = ['Connect Assistance'];

// ─── Result Card Logic ────────────────────────────────────────────────────────
function getResult(answers: Record<string, number>) {
  let memoria = 0,
    atencion = 0,
    razonamiento = 0,
    base = 0;

  Object.entries(answers).forEach(([key, val]) => {
    if (key.startsWith('memoria')) memoria += val;
    else if (key.startsWith('atencion')) atencion += val;
    else if (key.startsWith('razonamiento')) razonamiento += val;
    else if (key.startsWith('base')) base += val;
  });

  const max = Math.max(memoria, atencion, razonamiento, base);

  if (max === 0) {
    return {
      card: '🌟 Perfil Equilibrado',
      color: '#2ecc71',
      diagnosis:
        'Tus niveles de desgaste son mínimos. Puedes utilizar la IA para escalar tu productividad general sin preocuparte por reparar un área específica.',
      scores: { memoria, atencion, razonamiento, base }
    };
  }

  if (max === atencion) {
    return {
      card: '🟡 Tarjeta Amarilla (Atención Saturada)',
      color: '#ffdf00',
      diagnosis:
        'Tu filtro de concentración está al límite. Gastas mucha energía intentando volver a enfocarte tras distracciones. Usa los LLMs para filtrar el "ruido". Pídele a la IA que extraiga solo lo más importante de textos largos antes de leerlos.',
      scores: { memoria, atencion, razonamiento, base }
    };
  }

  if (max === memoria) {
    return {
      card: '🔮 Tarjeta Magenta (Fuga de Memoria)',
      color: '#ea00d9',
      diagnosis:
        'Tienes dificultades para retener múltiples datos a la vez. Convierte a la IA en tu disco duro externo. Carga tus documentos largos y pídele que te recuerde los datos clave mientras trabajas.',
      scores: { memoria, atencion, razonamiento, base }
    };
  }

  if (max === base) {
    return {
      card: '🟠 Tarjeta Naranja (Base Cognitiva)',
      color: '#ff6b35',
      diagnosis:
        'Tus inteligencias base (kinestésica-manual, espacial-visual) requieren un canal distinto al texto. Apóyate en la IA multimodal: pídele diagramas, esquemas visuales y simulaciones paso a paso para aprovechar tu forma natural de aprender haciendo y viendo.',
      scores: { memoria, atencion, razonamiento, base }
    };
  }

  return {
    card: '🔷 Tarjeta Cian (Bloqueo Lógico)',
    color: '#00d2ff',
    diagnosis:
      'La estructura y la síntesis te están costando trabajo. Usa la IA como tu compañero de razonamiento. Pídele que te arme esquemas de dependencias antes de iniciar un proyecto.',
    scores: { memoria, atencion, razonamiento, base }
  };
}

// ─── Main Page Component ──────────────────────────────────────────────────────
export default function CognitiveFormPage() {
  const [formData, setFormData] = useState<FormData>({
    account: '',
    name: '',
    role: '',
    answers: {}
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState<{
    show: boolean;
    type: NotificationType;
    message: string;
  }>({ show: false, type: 'success', message: '' });

  const showNotification = (type: NotificationType, message: string) => {
    setNotification({ show: true, type, message });
    setTimeout(() => setNotification((n) => ({ ...n, show: false })), 4000);
  };

  const handleAnswerChange = (blockId: string, qIdx: number, value: number) => {
    setFormData((prev) => ({
      ...prev,
      answers: { ...prev.answers, [`${blockId}_${qIdx}`]: value }
    }));
  };

  const totalQuestions = BLOCKS.reduce((acc, b) => acc + b.questions.length, 0);
  const answeredCount = Object.keys(formData.answers).length;
  const allAnswered = answeredCount === totalQuestions;

  // ─── Progressive reveal (UI only — does NOT affect the submitted payload) ─────
  const isBlockComplete = (block: (typeof BLOCKS)[number]) =>
    block.questions.every((_, qIdx) => formData.answers[`${block.id}_${qIdx}`] !== undefined);

  const infoComplete = Boolean(formData.account && formData.name.trim() && formData.role.trim());

  // How many pillars are currently visible: the first unlocks once the
  // participant info is filled, and each subsequent one unlocks only when the
  // previous pillar has been fully answered.
  let unlockedCount = 0;
  if (infoComplete) {
    unlockedCount = 1;
    for (let i = 0; i < BLOCKS.length - 1; i += 1) {
      if (isBlockComplete(BLOCKS[i])) unlockedCount += 1;
      else break;
    }
  }

  const blockRefs = useRef<Array<HTMLDivElement | null>>([]);
  const prevUnlocked = useRef(0);

  // Smoothly bring a newly-revealed pillar into view.
  useEffect(() => {
    if (unlockedCount > prevUnlocked.current && unlockedCount > 1) {
      const el = blockRefs.current[unlockedCount - 1];
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    prevUnlocked.current = unlockedCount;
  }, [unlockedCount]);

  const handleSubmit = async () => {
    if (!formData.account) {
      showNotification('error', 'Selecciona una cuenta antes de enviar.');
      return;
    }
    if (!formData.name.trim()) {
      showNotification('error', 'Ingresa tu nombre.');
      return;
    }
    if (!formData.role.trim()) {
      showNotification('error', 'Ingresa tu rol en la empresa.');
      return;
    }
    if (!allAnswered) {
      showNotification('error', `Faltan ${totalQuestions - answeredCount} preguntas por responder.`);
      return;
    }

    const result = getResult(formData.answers);
    const payload = {
      account: formData.account,
      name: formData.name,
      role: formData.role,
      answers: formData.answers,
      result: {
        card: result.card,
        scores: result.scores
      },
      submittedAt: new Date().toISOString()
    };

    console.log('═══════════════════════════════════════════════');
    console.log('📋 COGNITIVE FORM PAYLOAD');
    console.log('═══════════════════════════════════════════════');
    console.log(JSON.stringify(payload, null, 2));
    console.log('═══════════════════════════════════════════════');

    setLoading(true);
    try {
      await saveCognitiveRecord({
        account: formData.account,
        name: formData.name,
        role: formData.role,
        memoryScore: result.scores.memoria,
        attentionScore: result.scores.atencion,
        reasoningScore: result.scores.razonamiento,
        baseScore: result.scores.base,
        resultCard: result.card,
        submittedAt: payload.submittedAt,
        rawAnswers: JSON.stringify(formData.answers)
      });
      showNotification('success', '¡Diagnóstico enviado y guardado correctamente!');
      setSubmitted(true);
    } catch (err) {
      console.error(err);
      showNotification('error', 'Error al guardar en Airtable. Revisa la consola.');
    } finally {
      setLoading(false);
    }
  };

  const result = submitted ? getResult(formData.answers) : null;

  return (
    <div className="min-h-screen py-6 sm:py-12 px-0 sm:px-4 overflow-x-hidden dark:bg-black-100">
      <Notification
        show={notification.show}
        type={notification.type}
        message={notification.message}
        onClose={() => setNotification((n) => ({ ...n, show: false }))}
      />

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-10">
          <AnimatePresence>
            {formData.account === 'Connect Assistance' && (
              <motion.img
                key="connect-logo"
                src={connectCognitiveAugment}
                alt="Connect Assistance"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.4 }}
                className="mx-auto mb-4 sm:mb-6 h-24 sm:h-40 md:h-56 w-auto object-contain"
              />
            )}
          </AnimatePresence>
          <motion.h2
            initial={{ opacity: 0, x: 0, skewX: 0 }}
            animate={{
              opacity: [0, 1, 0.3, 1, 0.6, 1],
              x: [0, -6, 6, -3, 3, 0],
              skewX: [0, 12, -12, 6, -4, 0],
              filter: [
                'drop-shadow(0 0 0 transparent)',
                'drop-shadow(-3px 0 0 #00fff9) drop-shadow(3px 0 0 #ff00c1)',
                'drop-shadow(3px 0 0 #00fff9) drop-shadow(-3px 0 0 #ff00c1)',
                'drop-shadow(-2px 0 0 #00fff9) drop-shadow(2px 0 0 #ff00c1)',
                'drop-shadow(0 0 0 transparent)'
              ]
            }}
            transition={{ duration: 0.9, times: [0, 0.2, 0.4, 0.6, 0.8, 1], ease: 'easeInOut' }}
            className="text-xl leading-tight sm:text-4xl md:text-5xl font-bold text-white px-2"
          >
            Aumento Cognitivo: Tu primer chip de IA
          </motion.h2>
          <p className="text-gray-400 mt-2 sm:mt-3 text-xs sm:text-sm md:text-base px-2">
            Identifica qué área te consume más energía para aplicar la estrategia de IA correcta.
          </p>
        </div>

        {/* User Info Section */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="p-4 sm:p-6 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm mb-8 space-y-4"
        >
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <span>👤</span> Información del Participante
          </h2>

          {/* Account Selector */}
          <div>
            <label htmlFor="account-select" className="block text-sm text-gray-400 mb-1">
              Cuenta
            </label>
            <select
              id="account-select"
              value={formData.account}
              onChange={(e) => setFormData((p) => ({ ...p, account: e.target.value }))}
              className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:border-indigo-500 focus:outline-none transition-colors appearance-none cursor-pointer"
            >
              <option value="" className="bg-gray-900">
                -- Selecciona una cuenta --
              </option>
              {ACCOUNTS.map((acc) => (
                <option key={acc} value={acc} className="bg-gray-900">
                  {acc}
                </option>
              ))}
            </select>
          </div>

          {/* Name */}
          <div>
            <label htmlFor="name-input" className="block text-sm text-gray-400 mb-1">
              Nombre
            </label>
            <input
              id="name-input"
              type="text"
              placeholder="Tu nombre completo"
              value={formData.name}
              onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
              className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-600 focus:border-indigo-500 focus:outline-none transition-colors"
            />
          </div>

          {/* Role */}
          <div>
            <label htmlFor="role-input" className="block text-sm text-gray-400 mb-1">
              Rol en la empresa
            </label>
            <input
              id="role-input"
              type="text"
              placeholder="Ej: Frontend Developer, Project Manager..."
              value={formData.role}
              onChange={(e) => setFormData((p) => ({ ...p, role: e.target.value }))}
              className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-600 focus:border-indigo-500 focus:outline-none transition-colors"
            />
          </div>
        </motion.div>

        {/* Scale Guide */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="p-5 rounded-2xl bg-indigo-500/5 border border-indigo-500/20 mb-8"
        >
          <p className="text-sm text-indigo-300 font-medium mb-2">¿Con qué frecuencia o intensidad te ocurre esto?</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-gray-400">
            <span>
              <span className="text-white font-bold">0</span> — No es un problema
            </span>
            <span>
              <span className="text-white font-bold">1</span> — Molestia leve
            </span>
            <span>
              <span className="text-white font-bold">2</span> — Freno constante
            </span>
            <span>
              <span className="text-white font-bold">3</span> — Bloqueo crítico
            </span>
          </div>
        </motion.div>

        {/* Prompt to complete participant info before the diagnosis starts */}
        <AnimatePresence>
          {!infoComplete && (
            <motion.div
              key="info-hint"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="mb-8 p-5 rounded-2xl border border-dashed border-indigo-500/25 bg-indigo-500/5 text-center"
            >
              <p className="text-sm text-indigo-300">Completa tu información arriba para comenzar el diagnóstico. 👆</p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Question Blocks — revealed one pillar at a time */}
        <AnimatePresence initial={false}>
          {infoComplete &&
            BLOCKS.slice(0, unlockedCount).map((block, blockIdx) => {
              const answeredInBlock = block.questions.filter(
                (_, qIdx) => formData.answers[`${block.id}_${qIdx}`] !== undefined
              ).length;
              const blockComplete = answeredInBlock === block.questions.length;

              return (
                <motion.div
                  key={block.id}
                  ref={(el) => {
                    blockRefs.current[blockIdx] = el;
                  }}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.5, ease: 'easeOut' }}
                  className="mb-8 p-4 sm:p-6 rounded-2xl bg-white/[0.03] border"
                >
                  <div className="flex items-start justify-between gap-3 mb-1">
                    <h3 className="text-xl font-bold" style={{ color: block.color }}>
                      {block.title}
                    </h3>
                    <div className="flex items-center gap-2 shrink-0">
                      <span
                        className="text-[11px] font-semibold px-2.5 py-1 rounded-full"
                        style={{ backgroundColor: `${block.color}22`, color: block.color }}
                      >
                        {answeredInBlock}/{block.questions.length}
                      </span>
                      <AnimatePresence>
                        {blockComplete && (
                          <motion.span
                            initial={{ scale: 0, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0, opacity: 0 }}
                            transition={{ type: 'spring', stiffness: 400, damping: 18 }}
                            className="grid place-items-center h-6 w-6 rounded-full text-black text-xs font-bold"
                          >
                            ✓
                          </motion.span>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mb-5">{block.description}</p>

                  <div className="space-y-5">
                    {block.questions.map((question, qIdx) => {
                      const answered = formData.answers[`${block.id}_${qIdx}`] !== undefined;
                      return (
                        <div
                          key={qIdx}
                          className="p-4 rounded-xl bg-white/[0.02] border transition-colors"
                          style={{ borderColor: answered ? `${block.color}33` : 'rgba(255,255,255,0.05)' }}
                        >
                          <p className="text-sm text-gray-300 mb-3">{question}</p>
                          <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
                            {SCALE_OPTIONS.map((opt) => {
                              const isSelected = formData.answers[`${block.id}_${qIdx}`] === opt.value;
                              return (
                                <button
                                  key={opt.value}
                                  type="button"
                                  onClick={() => handleAnswerChange(block.id, qIdx, opt.value)}
                                  className={`w-full sm:w-auto flex items-center justify-center text-center gap-1 px-3 sm:px-4 py-3 sm:py-2 min-h-[44px] rounded-lg text-xs font-medium transition-all duration-200 active:scale-95 ${
                                    isSelected
                                      ? 'text-white shadow-lg sm:scale-105'
                                      : 'bg-white/5 text-gray-400 hover:bg-white/10 sm:hover:scale-[1.03] border border-white/10'
                                  }`}
                                  style={
                                    isSelected
                                      ? {
                                          backgroundColor: `${block.color}33`,
                                          border: `1px solid ${block.color}88`,
                                          boxShadow: `0 4px 14px -4px ${block.color}`
                                        }
                                      : undefined
                                  }
                                >
                                  <span className="font-bold">{opt.short}</span>
                                  <span className="opacity-80">— {opt.label}</span>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </motion.div>
              );
            })}
        </AnimatePresence>

        {/* Locked next-pillar hint */}
        <AnimatePresence>
          {infoComplete && unlockedCount > 0 && unlockedCount < BLOCKS.length && (
            <motion.div
              key="next-hint"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="mb-8 p-4 rounded-2xl border border-dashed border-white/10 bg-white/[0.02] flex items-center gap-3"
            >
              <span className="text-lg">🔒</span>
              <p className="text-xs text-gray-500">
                Completa
                <span className="text-gray-300 font-medium">{BLOCKS[unlockedCount - 1].title}</span> para desbloquear el
                siguiente pilar.
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Progress & Submit */}
        <AnimatePresence>
          {infoComplete && (
            <div className="sticky bottom-4 z-40">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                className="p-4 rounded-2xl bg-black-100/90 backdrop-blur-md border border-white/10 flex items-center justify-between gap-4"
              >
                <div className="flex-1">
                  <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                    <motion.div
                      className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-purple-500"
                      animate={{ width: `${(answeredCount / totalQuestions) * 100}%` }}
                      transition={{ duration: 0.3 }}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {answeredCount}/{totalQuestions} respuestas
                    {allAnswered && <span className="text-green-400 font-medium"> · ¡Listo para enviar!</span>}
                  </p>
                </div>
                <button
                  onClick={handleSubmit}
                  disabled={submitted || loading || !allAnswered}
                  className={`shrink-0 whitespace-nowrap px-4 sm:px-6 py-3 min-h-[44px] rounded-xl font-bold text-xs sm:text-sm transition-all ${
                    submitted
                      ? 'bg-green-500/20 text-green-300 cursor-default'
                      : loading
                        ? 'bg-indigo-500/20 text-indigo-300 cursor-wait'
                        : !allAnswered
                          ? 'bg-white/5 text-gray-500 border border-white/10 cursor-not-allowed'
                          : 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:from-indigo-500 hover:to-purple-500 hover:shadow-lg hover:shadow-indigo-500/25'
                  }`}
                >
                  {submitted ? '✓ Enviado' : loading ? '⏳ Guardando...' : 'Enviar Diagnóstico'}
                </button>
              </motion.div>
            </div>
          )}
        </AnimatePresence>

        {/* Result Card */}
        <AnimatePresence>
          {result && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="mt-10 p-5 sm:p-8 rounded-2xl border-l-4"
            >
              <h3 className="text-xl sm:text-2xl font-bold mb-4" style={{ color: result.color }}>
                {result.card}
              </h3>
              <p className="text-gray-300 text-sm leading-relaxed">{result.diagnosis}</p>
              <div className="mt-6 pt-4 border-t border-white/10 grid grid-cols-2 gap-3 sm:flex sm:flex-wrap sm:gap-6 text-xs text-gray-500">
                <span>
                  Memoria: <span className="text-white font-bold">{result.scores.memoria}</span>
                </span>
                <span>
                  Atención: <span className="text-white font-bold">{result.scores.atencion}</span>
                </span>
                <span>
                  Sistema 2: <span className="text-white font-bold">{result.scores.razonamiento}</span>
                </span>
                <span>
                  Base Cognitiva: <span className="text-white font-bold">{result.scores.base}</span>
                </span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}
